import React, { useMemo, useRef, useCallback } from "react";

/**
 * Renders a raw HTML string and properly executes any embedded <script> tags.
 *
 * Background / root cause of the "tabs not clickable" bug:
 * ----------------------------------------------------------------------------
 * Browsers deliberately do NOT execute <script> tags that are injected via the
 * `innerHTML` property - this applies whether the HTML is set via plain DOM
 * (`element.innerHTML = html`) OR via React's `dangerouslySetInnerHTML`. The
 * <script> element is inserted into the DOM tree, but its JS is never run.
 *
 * The Perceptra "Office FI / Residence FI" tab output relies on an inline
 * <script> that defines `switchTab()` (and `pcCopy()` for the click-to-copy
 * chips). When that HTML is dropped into the page via `dangerouslySetInnerHTML`
 * (as our React components do), the buttons render fine but
 * `onclick="switchTab('office')"` throws "switchTab is not defined" (silently,
 * since it's an inline handler) because the script that defines it never ran.
 * This is why the tabs work when the raw HTML file is opened directly in a
 * browser/HTML editor (normal HTML parsing DOES execute <script> tags) but
 * not when rendered inside the React app.
 *
 * Security note (XSS remediation):
 * ----------------------------------------------------------------------------
 * The previous implementation injected the raw HTML into the parent document
 * via `container.innerHTML = html` and then re-created every <script> tag so
 * the browser would execute it. That is an XSS anti-pattern: any script in the
 * `html` prop ran with full access to the parent application's DOM, cookies,
 * and origin.
 *
 * This component now renders the HTML inside a **sandboxed iframe** instead.
 * The embedded <script> tags still execute (preserving tab-switching,
 * copy-to-clipboard, etc.), but they run in an isolated browsing context. A
 * malicious script is therefore contained to the iframe and cannot reach the
 * parent application's DOM, storage, or cookies. This is the same sandboxing
 * pattern already used for Commercial CIBIL credit reports and Folium maps
 * elsewhere in this codebase.
 *
 * NOTE: `allow-same-origin` is intentionally included so the embedded scripts
 * (e.g. the tab-switcher) can access the same-origin static assets they rely
 * on. If the `html` prop ever contains untrusted third-party content, remove
 * `allow-same-origin` to further harden the sandbox.
 */
const HtmlContent = ({ html, className = "", style }) => {
	// Wrap partial HTML (not a full document) in a complete HTML document so
	// the browser parses and executes it exactly like a standalone page.
	const srcDoc = useMemo(() => {
		const content = html || "";
		if (/<!DOCTYPE html>/i.test(content) || /<html[\s>]/i.test(content)) {
			return content;
		}
		return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
  </head>
  <body>
    ${content}
  </body>
</html>`;
	}, [html]);

	const iframeRef = useRef(null);

	// Auto-size the iframe to the height of its content so the page scrolls
	// naturally (matching the pre-sandbox behavior where content flowed in the
	// parent document) instead of showing a nested scrollbar inside the iframe.
	// The iframe is sandboxed with allow-same-origin, so we can read its
	// contentDocument to measure the body height.
	const handleLoad = useCallback(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;
		try {
			const doc = iframe.contentDocument || iframe.contentWindow?.document;
			if (!doc) return;
			const body = doc.body;
			const htmlEl = doc.documentElement;
			if (!body) return;
			// Use the larger of body.scrollHeight / documentElement.scrollHeight
			// to account for margins and content that overflows the body.
			const height = Math.max(
				body.scrollHeight,
				body.offsetHeight,
				htmlEl.scrollHeight,
				htmlEl.offsetHeight
			);
			if (height > 0) {
				iframe.style.height = `${height}px`;
			}
		} catch (e) {
			// Cross-origin access is blocked; fall back to a reasonable height.
			console.warn("[HtmlContent] Could not auto-size iframe:", e);
		}
	}, []);

	return (
		<div className={className} style={style}>
			<iframe
				ref={iframeRef}
				srcDoc={srcDoc}
				title="Embedded content"
				sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals"
				onLoad={handleLoad}
				style={{
					width: "100%",
					height: "100%",
					border: "none",
					borderRadius: "4px",
				}}
			/>
		</div>
	);
};

export default HtmlContent;
